/**
 * Store hours override utility for special dates
 *
 * This file handles date-specific store hour overrides for holidays and special events.
 * Hours automatically revert to normal after the override date passes.
 */

export interface StoreHoursOverride {
  hours: string;
  openTime: string;
  closeTime: string;
}

/**
 * Special hours for holidays and events
 * Format: YYYY-MM-DD
 */
const SPECIAL_DATES: Record<string, Record<string, StoreHoursOverride>> = {
  '2025-11-27': {
    // Thanksgiving (11/27)
    catalystantioch: { hours: 'Open 8:00 AM – 6:00 PM', openTime: '08:00', closeTime: '18:00' },
    catalystbellflower: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystbelmont: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalyst405: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystcostamesa: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystdalycity: { hours: 'Open 10:00 AM – 6:00 PM', openTime: '10:00', closeTime: '18:00' },
    catalystpine: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystelmonte: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystfigueroa: { hours: 'Open 10:00 AM – 6:00 PM', openTime: '10:00', closeTime: '18:00' },
    catalystflorence: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystgroverbeach: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalysthawthorne: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalysthemet: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystlancaster: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystlynwood: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystmarina: { hours: 'Open 10:00 AM – 6:00 PM', openTime: '10:00', closeTime: '18:00' },
    catalystmidcity: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystmorenovalley: {
      hours: 'Open 10:00 AM – 6:00 PM',
      openTime: '10:00',
      closeTime: '18:00',
    },
    catalystnormandie: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    oc3: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystoxnard: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystpalmdesert: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystpatterson: { hours: 'Open 10:00 AM – 6:00 PM', openTime: '10:00', closeTime: '18:00' },
    catalystpomona: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystlift: { hours: 'Open 12:00 PM – 6:00 PM', openTime: '12:00', closeTime: '18:00' },
    catalystsantaana: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystsbinland: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystsandiego: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystsilverlake: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalyststanton: { hours: 'Open 9:00 AM – 6:00 PM', openTime: '09:00', closeTime: '18:00' },
    catalystvannuys: { hours: 'Open 10:00 AM – 6:00 PM', openTime: '10:00', closeTime: '18:00' },
    catalystwatsonville: {
      hours: 'Open 10:00 AM – 6:00 PM',
      openTime: '10:00',
      closeTime: '18:00',
    },
  },
  '2025-11-28': {
    // Black Friday (11/28) - Normal hours resume
    // Most stores return to normal hours, but Retro Row has special hours
    catalystlift: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
  },
  '2026-04-19': {
    // 4/20 Weekend — Retro Row opens early at 9am
    catalystlift: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
  },
  '2026-04-20': {
    // 4/20 Day — Retro Row opens early at 9am
    catalystlift: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
  },
  '2025-12-24': {
    // Kushmas Eve (12/24/25)
    catalystantioch: { hours: 'Open 8:00 AM – 8:00 PM', openTime: '08:00', closeTime: '20:00' },
    catalystbellflower: { hours: 'Open 9:00 AM – 9:00 PM', openTime: '09:00', closeTime: '21:00' },
    catalystbelmont: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalyst405: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystcostamesa: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystdalycity: { hours: 'Open 10:00 AM – 9:30 PM', openTime: '10:00', closeTime: '21:30' },
    catalystpine: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystelmonte: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystfigueroa: { hours: 'Open 10:00 AM – 9:30 PM', openTime: '10:00', closeTime: '21:30' },
    catalystflorence: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystgroverbeach: { hours: 'Open 9:00 AM – 9:00 PM', openTime: '09:00', closeTime: '21:00' },
    catalysthawthorne: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalysthemet: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystlancaster: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystlynwood: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystmarina: { hours: 'Open 10:00 AM – 9:00 PM', openTime: '10:00', closeTime: '21:00' },
    catalystmidcity: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystmorenovalley: {
      hours: 'Open 10:00 AM – 9:30 PM',
      openTime: '10:00',
      closeTime: '21:30',
    },
    catalystnormandie: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    oc3: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystoxnard: { hours: 'Open 9:00 AM – 9:00 PM', openTime: '09:00', closeTime: '21:00' },
    catalystpalmdesert: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystpatterson: { hours: 'Open 10:00 AM – 9:00 PM', openTime: '10:00', closeTime: '21:00' },
    catalystpomona: { hours: 'Open 9:00 AM – 9:50 PM', openTime: '09:00', closeTime: '21:50' },
    catalystlift: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystsantaana: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystsbinland: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystsandiego: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystsilverlake: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalyststanton: { hours: 'Open 9:00 AM – 9:30 PM', openTime: '09:00', closeTime: '21:30' },
    catalystvannuys: { hours: 'Open 10:00 AM – 9:30 PM', openTime: '10:00', closeTime: '21:30' },
    catalystwatsonville: {
      hours: 'Open 10:00 AM – 9:00 PM',
      openTime: '10:00',
      closeTime: '21:00',
    },
  },
  '2025-12-25': {
    // Kushmas Day (12/25/25)
    catalystantioch: { hours: 'Open 12:00 PM – 8:00 PM', openTime: '12:00', closeTime: '20:00' },
    catalystbellflower: { hours: 'Open 12:00 PM – 9:00 PM', openTime: '12:00', closeTime: '21:00' },
    catalystbelmont: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalyst405: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystcostamesa: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystdalycity: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystpine: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystelmonte: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystfigueroa: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystflorence: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystgroverbeach: {
      hours: 'Open 12:00 PM – 9:00 PM',
      openTime: '12:00',
      closeTime: '21:00',
    },
    catalysthawthorne: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalysthemet: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystlancaster: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystlynwood: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystmarina: { hours: 'Open 12:00 PM – 9:00 PM', openTime: '12:00', closeTime: '21:00' },
    catalystmidcity: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystmorenovalley: {
      hours: 'Open 12:00 PM – 9:30 PM',
      openTime: '12:00',
      closeTime: '21:30',
    },
    catalystnormandie: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    oc3: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystoxnard: { hours: 'Open 12:00 PM – 9:00 PM', openTime: '12:00', closeTime: '21:00' },
    catalystpalmdesert: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystpatterson: { hours: 'Open 12:00 PM – 9:00 PM', openTime: '12:00', closeTime: '21:00' },
    catalystpomona: { hours: 'Open 12:00 PM – 9:50 PM', openTime: '12:00', closeTime: '21:50' },
    catalystlift: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystsantaana: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystsbinland: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystsandiego: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystsilverlake: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalyststanton: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystvannuys: { hours: 'Open 12:00 PM – 9:30 PM', openTime: '12:00', closeTime: '21:30' },
    catalystwatsonville: {
      hours: 'Open 12:00 PM – 9:00 PM',
      openTime: '12:00',
      closeTime: '21:00',
    },
  },
};

/**
 * Get today's date in YYYY-MM-DD format (Pacific Time - PST/PDT)
 * Uses America/Los_Angeles timezone which automatically handles daylight saving time
 * Returns empty string if date parsing fails (safety fallback)
 */
function getTodayDateString(): string {
  try {
    // Get current date in Pacific Time (PST/PDT) timezone
    // Using Intl.DateTimeFormat for reliable timezone conversion
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(now);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';

    // Validate we have all parts before returning
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }

    // Fallback: return empty string if parsing fails (will result in no override)
    return '';
  } catch (error) {
    // Safety fallback: if anything goes wrong, return empty string
    // This ensures the function never throws and always returns a valid string
    console.error("Error getting today's date:", error);
    return '';
  }
}

/**
 * Get store hours override for a specific store on today's date
 * @param storeValue The store value (e.g., 'catalystbellflower')
 * @returns Override hours if today is a special date, null otherwise
 */
export function getStoreHoursOverride(storeValue: string): StoreHoursOverride | null {
  const today = getTodayDateString();
  const todayOverrides = SPECIAL_DATES[today];

  if (!todayOverrides) {
    return null;
  }

  return todayOverrides[storeValue] || null;
}

/**
 * Check if today is a special date with hour overrides
 */
export function isSpecialDate(): boolean {
  const today = getTodayDateString();
  return SPECIAL_DATES.hasOwnProperty(today);
}
